import {
  CardViewEnum,
  EMPTY_ARRAY,
  isMultileg,
  type MarketAccount,
  type MarketDataCardConfig,
  MarketTypeEnum,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  useSecurity,
  useUnifiedLiquidityContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { getSymbolAndMarketsNoUnifiedLiquidity } from '../../utils/symbolAndMarketsNoUnifiedLiquidity';
import { filterMarketAccounts, filterMarkets } from '../MarketAccountSelectionList/utils';
import { useMarketSelectorBuyingPowerEnrichment } from '../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketDataMarketSelectorItems } from '../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../MarketSelector/types';
import { useMarketSelector, type UseMarketSelectorParams } from '../MarketSelector/useMarketSelector';
import { marketSelectorItemsToOldItems } from '../MarketSelector/utils/items';
import { removeAnyMultipleSelectionsPerMarket } from '../MarketSelector/utils/OrderMarketsField';
import { useAllowMultipleAcctsPerCred } from '../MarketSelector/utils/useAllowMultipleAcctsPerCred';
import { useMarketDataCardSymbolChangeSelections } from './useMarketDataCardSymbolChangeSelections';

interface SettingsForm {
  symbol: string | undefined;
  title: string;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation: string | undefined;
  view: CardViewEnum;
}

interface UseMarketDataCardSettingsParams {
  config: MarketDataCardConfig;
}

/**
 * This hook houses a bunch of business logic for the settings available on the Market Data Card Settings slide
 * The logic is put into this hook so that it can be more easily unit tested
 */
export const useMarketDataCardSettings = ({ config }: UseMarketDataCardSettingsParams) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const getSymbolChangeSelections = useMarketDataCardSymbolChangeSelections();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const [openMarketAccountSelector, setOpenMarketAccountSelector] = useState(false);

  const [ulEnabled, setULEnabled] = useState(config.unifiedLiquidity === UnifiedLiquidityEnum.Enabled);
  const [enableShowBBO, setShowBBO] = useState(config.alwaysDisplayBestBidOffer === true);

  const [form, setForm] = useState<SettingsForm>({
    symbol: config.symbol,
    title: config.title || '',
    selectedMarkets: filterMarkets(config.selectedMarkets, marketAccountsByMarket),
    selectedMarketAccounts: filterMarketAccounts(
      config.selectedMarketAccounts,
      config.selectedMarkets,
      marketAccountsByMarket,
      marketAccountsByName
    ),
    // If we for example disable the new market selector due to some problem, we make sure we dont use this config for anything
    selectedAggregation: useNewMarketSelector ? config.selectedAggregation : undefined,
    view: config.view || CardViewEnum.NORMAL,
  });

  const security = useSecurity(form.symbol);

  const updateForm = useCallback((update: Partial<SettingsForm>) => {
    setForm(prev => ({ ...prev, ...update }));
  }, []);

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      const ulResponse = symbol ? unifiedLiquidityTokenBySymbol?.get(symbol) : undefined;
      // Try to keep the UL setting. If UL isn't applicable to the new symbol however (no ulResponse), make sure it is set to false.
      const newULEnabled = ulEnabled && !!ulResponse;
      setULEnabled(newULEnabled);

      const selections = getSymbolChangeSelections(symbol, newULEnabled);
      if (!selections) {
        return;
      }

      updateForm({
        symbol,
        selectedMarkets: selections.selectedMarkets,
        selectedMarketAccounts: selections.selectedMarketAccounts,
        selectedAggregation: selections.selectedAggregation,
      });
    },
    [ulEnabled, unifiedLiquidityTokenBySymbol, getSymbolChangeSelections, updateForm]
  );

  const isMarketDark = useCallback(
    (marketName: string | undefined) => {
      const market = marketName ? marketsByName.get(marketName) : undefined;
      return market ? market.Type === MarketTypeEnum.Dark : false;
    },
    [marketsByName]
  );

  const isMarketAccountDark = useCallback(
    (marketAccountName: string | undefined) => {
      const market = marketAccountName ? marketAccountsByName.get(marketAccountName)?.Market : undefined;
      return market ? isMarketDark(market) : false;
    },
    [marketAccountsByName, isMarketDark]
  );

  /**
   * When a new Market or MarketAcccount selection is made, that new selection might include Dark markets
   * For any selection, we only allow ONLY Dark markets, or NO Dark markets at all in the selections.
   */
  const getOnlyDarkOrNoDarkSelections = useCallback(
    (latestSelectionIsDark: boolean, selections: { markets: string[]; marketAccounts: string[] }) => {
      return {
        marketAccounts: selections.marketAccounts.filter(ma =>
          latestSelectionIsDark ? isMarketAccountDark(ma) : !isMarketAccountDark(ma)
        ),
        markets: selections.markets.filter(m => (latestSelectionIsDark ? isMarketDark(m) : !isMarketDark(m))),
      };
    },
    [isMarketAccountDark, isMarketDark]
  );

  const handleMarketAccountsChange = useCallback(
    (newSelectedMarketAccounts: string[]) => {
      // You can only have dark or non-dark selected. Would be nice to build support for this kind of situation into useMarketSelector.
      const newSelections = getOnlyDarkOrNoDarkSelections(isMarketAccountDark(newSelectedMarketAccounts.at(-1)), {
        marketAccounts: newSelectedMarketAccounts,
        markets: form.selectedMarkets,
      });

      updateForm({ selectedMarketAccounts: newSelections.marketAccounts, selectedMarkets: newSelections.markets });
    },
    [updateForm, getOnlyDarkOrNoDarkSelections, isMarketAccountDark, form.selectedMarkets]
  );

  const handleMarketsChange = useCallback(
    (newSelectedMarkets: string[]) => {
      // You can only have dark or non-dark selected. Would be nice to build support for this kind of situation into useMarketSelector.
      const newSelections = getOnlyDarkOrNoDarkSelections(isMarketDark(newSelectedMarkets.at(-1)), {
        marketAccounts: form.selectedMarketAccounts,
        markets: newSelectedMarkets,
      });

      updateForm({ selectedMarketAccounts: newSelections.marketAccounts, selectedMarkets: newSelections.markets });
    },
    [updateForm, getOnlyDarkOrNoDarkSelections, isMarketDark, form.selectedMarketAccounts]
  );

  /* Start - New Market Selector specifics */
  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol: security?.Symbol,
    includeUnifiedLiquidityMarkets: ulEnabled,
  });

  // We still support the old market selector. We need to map from new MarketSelectorItem[] -> { markets: string[], marketAccounts: string[] }
  const oldMarketSelectorItems = useMemo(() => {
    return marketSelectorItemsToOldItems({ items: marketSelectorItems, includeInactiveMarketAccounts: true });
  }, [marketSelectorItems]);

  const newMarketSelectorValue: MarketSelectorValue = useMemo(() => {
    const selections = [...form.selectedMarkets, ...form.selectedMarketAccounts];
    return form.selectedAggregation
      ? { type: 'preset', presetID: form.selectedAggregation, selections }
      : { type: 'custom', selections };
  }, [form.selectedMarketAccounts, form.selectedMarkets, form.selectedAggregation]);

  /**
   * If you just have a flat list of selections that are a mix of market and account names, this'll check if the
   * selection at the last index is of a dark market or not
   */
  const isLatestSelectionDark = useCallbackRef((selections: string[]) => {
    const latestSelection = selections.at(-1);
    if (!latestSelection) {
      return false;
    }

    // Its either a market name or a market account name
    const isMarketName = marketsByName.has(latestSelection);
    return isMarketName ? isMarketDark(latestSelection) : isMarketAccountDark(latestSelection);
  });

  // useCallbackRef to not have to react to ref data set updates
  const handleNewMarketSelectorChange = useCallbackRef((value: MarketSelectorValue) => {
    const selections = value.selections;
    const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
    const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));

    /** Because in the new market selector you can select all across market types, this is not fool-proof. Only works if you
     * select one market at a time. We need to improve this in the future.
     */
    const allDarkOrNoDarkNewSelections = getOnlyDarkOrNoDarkSelections(isLatestSelectionDark(selections), {
      markets,
      marketAccounts,
    });

    updateForm({
      selectedAggregation: value.type === 'preset' ? value.presetID : undefined,
      selectedMarkets: allDarkOrNoDarkNewSelections.markets,
      selectedMarketAccounts: allDarkOrNoDarkNewSelections.marketAccounts,
    });
  });

  const unsettledEnrichment = useUnsettledEnrichment({ security, marketSelectorItems });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'MarketDataCardMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const allowMultipleAcctsPerCred = useAllowMultipleAcctsPerCred({
    symbol: security?.Symbol,
    ulEnabled: ulEnabled,
  });
  const marketSelectorOptions: UseMarketSelectorParams['options'] = useMemo(
    () => ({ allowMultipleAcctsPerCred }),
    [allowMultipleAcctsPerCred]
  );

  const marketSelector = useMarketSelector({
    value: newMarketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    items: marketSelectorItems,
    presets: aggregationPresets,
    enrichmentSpecs,
    options: marketSelectorOptions,
  });
  /* End - new market selector specifics */

  const handleChangeUnifiedLiquidity = useCallbackRef((unifiedLiquidityEnabled: boolean) => {
    if (!security) {
      return;
    }

    setULEnabled(unifiedLiquidityEnabled);
    // Force-open the market selector when enabling UL
    if (unifiedLiquidityEnabled) {
      setOpenMarketAccountSelector(true);
    }

    if (isMultileg(security)) {
      // Might be a bit confusing but it doesn't matter if we're enabling or disabling UL for ML - we can always set this and it is correct.
      updateForm({
        selectedMarkets: [],
        selectedMarketAccounts: [MULTILEG_MARKET_ACCOUNT],
      });
      return;
    }

    // Non-multileg below. Only case that we need to look at is disabling UL.
    if (!unifiedLiquidityEnabled) {
      // If UL was disabled, and we are NOT a multileg market data card, then we have to remove the UL markets and accounts (if any)
      const nonULSelections = getSymbolAndMarketsNoUnifiedLiquidity({
        security,
        marketAccountsByName,
        marketSelections: form.selectedMarkets,
        marketAccountSelections: form.selectedMarketAccounts,
      });

      // There is also a situation where the client can have selected several market accounts per market. This is allowed for certain clients when UL is enabled.
      // If this is the case, then we also make sure to remove these. We use the current marketSelectorItems to decide which of the multiple valid
      // selections to stick with. We can still use the marketSelectorItems list _with UL_ (they haven't recomputed yet) since we just don't look at the UL items.
      if (hasMultipleMarketAccountsPerMarket(nonULSelections.marketAccounts, marketAccountsByName)) {
        nonULSelections.marketAccounts = removeAnyMultipleSelectionsPerMarket({
          selections: nonULSelections.marketAccounts,
          items: marketSelectorItems,
        });
      }

      updateForm({
        selectedMarkets: nonULSelections.markets,
        selectedMarketAccounts: nonULSelections.marketAccounts,
      });
    }
  });

  const handleMultilegULMarketsChange = useCallbackRef((marketAccounts: string[]) => {
    handleMarketAccountsChange([...marketAccounts, MULTILEG_MARKET_ACCOUNT]);
  });

  return {
    form,
    ulEnabled,
    enableShowBBO,
    openMarketAccountSelector,
    marketSelector,
    oldMarketSelectorItems,
    onChangeUnifiedLiquidity: handleChangeUnifiedLiquidity,
    setShowBBO,
    updateForm,
    onSymbolChange: handleSymbolChange,
    onMultilegULMarketsChange: handleMultilegULMarketsChange,
    onMarketsChange: handleMarketsChange,
    onMarketAccountsChange: handleMarketAccountsChange,
  };
};

/** Simple utility that just checks if your array of market account names has any occurrence of 2+ accounts per market in it */
function hasMultipleMarketAccountsPerMarket(
  marketAccountNames: string[],
  marketAccountsByName: Map<string, MarketAccount>
): boolean {
  const marketsFound = new Set();
  for (const marketAccountName of marketAccountNames) {
    const marketAccount = marketAccountsByName.get(marketAccountName);
    if (!marketAccount) {
      continue; // ignore
    }

    if (marketsFound.has(marketAccount.Market)) {
      return true;
    }

    marketsFound.add(marketAccount.Market);
  }

  return false;
}
